/**
 * Opens an external link reliably.
 *
 * Inside the iOS app the link opens in an in-app browser sheet, so the member
 * can always close it and come straight back to Recovery Trauma™ instead of
 * being stranded on an outside website.
 *
 * On the web we open a new tab. Some environments (installed home-screen app,
 * in-app browsers, embedded previews) silently block `target="_blank"`
 * navigations; in that case we fall back to navigating the current tab so the
 * link always works.
 */
import { isNativeApp } from "./native-app";

export function openExternal(
  url: string,
  event?: { preventDefault: () => void },
) {
  if (typeof window === "undefined") return;

  if (isNativeApp()) {
    event?.preventDefault();
    void (async () => {
      try {
        const { Browser } = await import("@capacitor/browser");
        await Browser.open({ url, presentationStyle: "popover" });
        return;
      } catch {
        /* in-app browser unavailable: fall back below */
      }
      try {
        const win = window.open(url, "_blank");
        if (win) return;
      } catch {
        /* fall through */
      }
      window.location.href = url;
    })();
    return;
  }

  try {
    const win = window.open(url, "_blank");
    if (win) {
      try {
        win.opener = null;
      } catch {
        /* cross-origin window: opener already restricted */
      }
      event?.preventDefault();
      return;
    }
  } catch {
    /* fall through */
  }
  // Popup was blocked: navigate the current tab instead.
  event?.preventDefault();
  window.location.href = url;
}
